/**
 * Confusion-matrix helpers: reading a task's `metrics.predicted` row and
 * deciding whether a whole group should render as a confusion matrix.
 */

import type { EvalTaskSummary } from '@/api/generated/model'

/** A task's confusion row as {predicted_label: count}, or null if absent. */
export function predictedRow(task: EvalTaskSummary): Record<string, number> | null {
  const predicted = task.metrics?.predicted
  if (predicted === null || typeof predicted !== 'object') return null
  const row: Record<string, number> = {}
  for (const [label, count] of Object.entries(predicted)) {
    if (typeof count !== 'number') return null
    row[label] = count
  }
  return row
}

/** True only when every task carries a numeric `metrics.predicted` row. */
export function isConfusionGroup(tasks: EvalTaskSummary[]): boolean {
  return tasks.length > 0 && tasks.every((task) => predictedRow(task) !== null)
}

/**
 * The ingest slug a pipeline would derive from a class label: lowercase,
 * non-slug characters collapsed to "-" (the API's SLUG_PATTERN allows
 * [a-z0-9.-], so dots survive). "DNA" → "dna", "Open Ended" → "open-ended".
 *
 * The confusion matrix joins predicted-label columns to task rows on this
 * slug, never on display_name — the slug is the identity a UI rename
 * (TRA-947) can never touch, so renaming a task cannot break its diagonal.
 */
export function slugifyLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
